import { Injectable } from '@angular/core';
import {
  ActivityLocation,
  IAccessRoute,
  IBoondock,
  IBoondockPoint,
  IListing,
  IListingPoint,
  ISite,
} from '@curbnturf/entities';
import { PlatformService } from '@curbnturf/network/src/lib/platform/platform.service';

@Injectable({
  providedIn: 'root',
})
export class NavigateToHelper {
  constructor(private platformService: PlatformService) {}

  makeNavigationLinkFromBoondockPoint(boondockPoint: IBoondockPoint): string {
    const boondock: IBoondock = { ...boondockPoint.boondock };
    if (!boondock.coordinates) {
      boondock.coordinates = boondockPoint.coordinates;
    }

    if (!boondock.name) {
      boondock.name = boondockPoint.name;
    }

    return this.makeNavigationLinkFromBoondock(boondock);
  }

  makeNavigationLinkFromBoondock(boondock: IBoondock): string {
    const waypoints: string[] = [];

    if (boondock?.accessRoutes?.length) {
      const accessRoute: IAccessRoute = boondock?.accessRoutes[0];

      if (accessRoute.startingPoint && accessRoute.startingPoint.lat && accessRoute.startingPoint.lon) {
        waypoints.push(`${accessRoute.startingPoint.lat},${accessRoute.startingPoint.lon}`);
      }
      if (accessRoute.intermediateWaypoints) {
        accessRoute.intermediateWaypoints.forEach((waypoint) => {
          if (waypoint.lat && waypoint.lon) {
            waypoints.push(`${waypoint.lat},${waypoint.lon}`);
          }
        });
      }
    }

    return this.getNavigateLink(
      boondock?.coordinates?.lat || 0,
      boondock?.coordinates?.lon || 0,
      boondock.name,
      waypoints,
    );
  }

  makeNavigationLinkFromListingPoint(listingPoint: IListingPoint) {
    const listing: IListing = { ...listingPoint.listing };
    if (!listing.coordinates) {
      listing.coordinates = listingPoint.coordinates;
    }

    if (!listing.name) {
      listing.name = listingPoint.name;
    }

    return this.makeNavigationLinkFromListing(listing);
  }

  makeNavigationLinkFromListing(listing: IListing) {
    const waypoints: string[] = [];

    return this.getNavigateLink(
      listing?.coordinates?.lat || 0,
      listing?.coordinates?.lon || 0,
      listing.name,
      waypoints,
    );
  }

  makeNavigationLinkFromSite(site: ISite) {
    const waypoints: string[] = [];

    if (site?.accessRoutes?.length) {
      const accessRoute: IAccessRoute = site?.accessRoutes[0];

      if (accessRoute.startingPoint && accessRoute.startingPoint.lat && accessRoute.startingPoint.lon) {
        waypoints.push(`${accessRoute.startingPoint.lat},${accessRoute.startingPoint.lon}`);
      }
      if (accessRoute.intermediateWaypoints) {
        accessRoute.intermediateWaypoints.forEach((waypoint) => {
          if (waypoint.lat && waypoint.lon) {
            waypoints.push(`${waypoint.lat},${waypoint.lon}`);
          }
        });
      }
    }

    return this.getNavigateLink(site?.coordinates?.lat || 0, site?.coordinates?.lon || 0, site.name, waypoints);
  }

  makeLinkFromBoondock(boondock: IBoondock) {
    const waypoints: string[] = [];

    if (boondock?.accessRoutes?.length) {
      const accessRoute: IAccessRoute = boondock?.accessRoutes[0];

      if (accessRoute.startingPoint && accessRoute.startingPoint.lat && accessRoute.startingPoint.lon) {
        waypoints.push(`${accessRoute.startingPoint.lat},${accessRoute.startingPoint.lon}`);
      }
      if (accessRoute.intermediateWaypoints) {
        accessRoute.intermediateWaypoints.forEach((waypoint) => {
          if (waypoint.lat && waypoint.lon) {
            waypoints.push(`${waypoint.lat},${waypoint.lon}`);
          }
        });
      }
    }

    return this.getNavigateLink(
      boondock?.coordinates?.lat || 0,
      boondock?.coordinates?.lon || 0,
      boondock.name,
      waypoints,
    );
  }

  makeLinkFromActivity(activity: ActivityLocation) {
    return this.getNavigateLink(activity.coordinates.lat, activity.coordinates.lon, activity.name);
  }

  getNavigateLink(lat: number | string, lng: number | string, label?: string, waypoints: string[] = []): string {
    const point = encodeURI(lat.toString()) + ',' + encodeURI(lng.toString());

    let intentUri: string;
    if (this.platformService.isIOS()) {
      intentUri = `maps://?daddr=${point}&dirflg=d&t=r`;
    } else if (this.platformService.isAndroid()) {
      intentUri = 'google.navigation:q=' + point;
    } else {
      intentUri = `https://www.google.com/maps/dir/?api=1&destination=${point}`;
      if (waypoints.length > 0) {
        intentUri += `&waypoints=${waypoints.join('|')}`;
      }
    }

    return intentUri;
  }
}
